import React, {
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect, {
  SelectChangeEvent,
} from '@mui/material/Select';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import Button from 'pages/Dashboard/components/Button';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import {
  getYearRange,
  getYearOptions,
  yearMenuProps,
} from 'core/components/DateTimePicker/base/calendarYearSelect';

type MonthYearPaperContentProps = {
  children: ReactNode;
  onChange?: (date: Dayjs) => void;
  value?: Dayjs | null;
};

export type MonthPickerClasses = {
  root?: string;
  input?: string;
  inputContainer?: string;
};

type Props = {
  value?: Dayjs | string | Date | null;
  onChange: (value: Dayjs | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  classes?: MonthPickerClasses;
};

function MonthYearPaperContent({
  children,
  onChange,
  value,
}: MonthYearPaperContentProps) {
  const selectedValue = value?.isValid() ? value : dayjs();
  const selectedMonth = selectedValue.month();
  const [displayYear, setDisplayYear] = useState(selectedValue.year());

  useEffect(() => {
    if (value?.isValid()) {
      setDisplayYear(value.year());
    }
  }, [value]);

  const months = useMemo(() => (
    Array.from({ length: 12 }, (_, index) => ({
      label: dayjs().month(index).format('MMM'),
      value: index,
    }))
  ), []);

  const yearOptions = useMemo(() => {
    const { startYear, endYear } = getYearRange(
      undefined,
      undefined,
      new Date(displayYear, selectedMonth, 1),
    );

    return getYearOptions(startYear, endYear);
  }, [displayYear, selectedMonth]);

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setDisplayYear(Number(event.target.value));
  };

  return (
    <div className="w-[320px] p-4">
      <div className="mb-3 flex items-center justify-between">
        <Button
          type="button"
          variant="outlined"
          className="!min-w-[32px] !h-8 !px-0 !border-0"
          onClick={() => { setDisplayYear((prev) => prev - 1); }}
        >
          <KeyboardArrowLeftIcon className="!h-5 !w-5 !text-gray-700" />
        </Button>
        <MuiSelect<number>
          aria-label="Select year"
          className="calendar-caption-select"
          value={displayYear}
          onChange={handleYearChange}
          size="small"
          variant="standard"
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={yearMenuProps}
        >
          {yearOptions.map((year) => (
            <MenuItem
              key={year}
              value={year}
              className="calendar-caption-option !text-xs !font-medium"
            >
              {year}
            </MenuItem>
          ))}
        </MuiSelect>
        <Button
          type="button"
          variant="outlined"
          className="!min-w-[32px] !h-8 !px-0 !border-0"
          onClick={() => { setDisplayYear((prev) => prev + 1); }}
        >
          <KeyboardArrowRightIcon className="!h-5 !w-5 !text-gray-700" />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {months.map((month) => {
          const isSelected = selectedValue.year() === displayYear
            && selectedValue.month() === month.value;

          return (
            <button
              key={month.value}
              type="button"
              className={clsx(
                'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                  { 'border-blue-500 bg-blue-50 text-blue-600': isSelected },
                  { 'border-gray-200 text-gray-700 hover:bg-gray-50': !isSelected },
              )}
              onClick={() => {
                onChange?.(dayjs(new Date(displayYear, month.value, 1)));
              }}
            >
              {month.label}
            </button>
          );
        })}
      </div>
      <div className="hidden">{children}</div>
    </div>
  );
}

export default function MonthPicker({
  value,
  onChange,
  onBlur,
  disabled = false,
  placeholder = 'MM/YYYY',
  classes,
}: Props) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      const inputLength = inputRef.current?.value.length ?? 0;
      inputRef.current?.setSelectionRange(inputLength, inputLength);
    });
  }, [isFocused]);

  return (
    <DatePicker
      accumulationUnit="month"
      inputRef={inputRef}
      value={value}
      onChange={onChange}
      onBlur={() => {
        setIsFocused(false);
        onBlur?.();
      }}
      disabled={disabled}
      containerClassName={clsx(
        'shrink-0 hover:!border-gray-600 focus-within:!border-gray-400',
        classes?.root,
      )}
      className={clsx('date-picker-input-semibold no-border', classes?.input)}
      inputContainerClassName={clsx('w-36', classes?.inputContainer)}
      inputFormat={isFocused ? 'MM/YYYY' : 'MMMM YYYY'}
      placeholder={isFocused ? 'MM/YYYY' : placeholder}
      inputProps={{
        onFocus: () => {
          setIsFocused(true);
        },
      }}
      showDateButtons={false}
      slots={{ desktopPaperContent: MonthYearPaperContent }}
    />
  );
}
